import './riskScoreDsWidget.less'
import {
  ACTION_DATA_SOURCES_RISK_SCORE,
  DataSourceRiskScore,
  fetchDataSourcesRiskScore
} from '../riskScoreSlice'
import { getShowLoader } from '../../../reducers/requestReducer'
import LoaderView from '../../../components/Loader/loader'
import { RootState } from '../../../rootReducer'
import NoDataFound from '../../../components/NoDataFound'
import { getDataSourceImage, getDatasourceUrl } from '../../../utils/dataSourceUtil'
import useDebouncedResize from '../../../hooks/useDebounceResize'
import { mockMainDashboardRiskScoreWidgetData } from '../../dashboards/renderer/utils/widgetMockdata'
import { changeUrl } from '../../../utils/urlUtil'
import { isRiskScoreEnabled } from '../riskScoreSelectors'
import React, { useEffect, useMemo } from 'react'
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { Typography } from '@lightbeamai/design-system'

const THRESOLD_LOW = 10000
const THRESOLD_MEDIUM = 1000000
const DEBOUNCE_DELAY = 500

const CustomScatterTooltip = (props) => {
  const intl = useIntl()

  if (props.active && props.payload && props.payload.length) {
    const ds: DataSourceRiskScore = props.payload[0].payload

    const densityLabel =
      ds.riskDensity < 10
        ? 'lowSensitive'
        : ds.riskDensity < 40
        ? 'mediumSensitive'
        : 'highSensitive'

    return (
      <div
        className="custom-scatter-tooltip"
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: '10px'
        }}
      >
        <Typography as={'div'} type="medium" variant="baseMinus2" className="ds-header">
          {ds.name}
        </Typography>
        <div className="tooltip-container row">
          <Typography as={'span'} type="normal" variant="baseMinus2">
            {intl.formatMessage({ id: 'riskScore' })}
          </Typography>
          <Typography as={'span'} type="medium" variant="baseMinus2">
            {intl.formatNumber(ds.totalRiskScore, { notation: 'compact' })}
          </Typography>
        </div>
        <div className="tooltip-container row">
          <Typography as={'span'} type="normal" variant="baseMinus2">
            {intl.formatMessage({ id: 'riskScore.density' }) +
              ` (${intl.formatMessage({ id: `accessControl.${densityLabel}` })})`}
          </Typography>
          <Typography as={'span'} type="medium" variant="baseMinus2">
            {`${ds.riskDensity}%`}
          </Typography>
        </div>
      </div>
    )
  }
  return null
}

const CustomScatterDsElement = (props) => {
  const { cx, cy, payload } = props
  const SIZE = 35
  const imageUrl = getDataSourceImage(payload.type, '/../../assets/images/dataSourceTypes/')

  // Calculate the top-left corner so the square is centered at (cx, cy)
  const borderRadius = 4
  const imagePadding = 6
  const imageSize = SIZE - 2 * imagePadding
  const dsRedirectUrl =
    payload?.type && payload?.id ? getDatasourceUrl(payload?.type, payload?.id) : ''

  const adjustedCy = payload?.riskDensity < 10 ? cy - SIZE / 2 : cy
  return (
    <g transform={`translate(${cx}, ${adjustedCy})`}>
      <rect
        x={-SIZE / 2}
        y={-SIZE / 2}
        width={SIZE}
        height={SIZE}
        rx={borderRadius}
        ry={borderRadius}
        fill="#F1F1F4"
        stroke="#DADCE2"
      />
      <a href="#" onClick={() => changeUrl(dsRedirectUrl, {}, true)}>
        <image
          xlinkHref={imageUrl}
          x={-SIZE / 2 + imagePadding}
          y={-SIZE / 2 + imagePadding}
          width={imageSize}
          height={imageSize}
        />
      </a>
    </g>
  )
}

interface OwnProps {
  useMockData?: boolean
  className?: string
}

interface StateProps {
  dataSourcesRiskScore?: DataSourceRiskScore[]
  isRiskScoreEnabled: boolean
  showLoader: boolean
}

interface DispatchProps {
  fetchDataSourcesRiskScore: () => void
}

type RiskScoreDsWidgetComponentProps = StateProps & DispatchProps & OwnProps

const RiskScoreDsWidget = (props: RiskScoreDsWidgetComponentProps) => {
  const {
    isRiskScoreEnabled,
    dataSourcesRiskScore,
    showLoader,
    useMockData = false,
    className = '',
    fetchDataSourcesRiskScore
  } = props

  const intl = useIntl()
  const resizeKey = useDebouncedResize(DEBOUNCE_DELAY)

  useEffect(() => {
    if (useMockData) return
    isRiskScoreEnabled && fetchDataSourcesRiskScore()
  }, [isRiskScoreEnabled, fetchDataSourcesRiskScore, useMockData])

  // Group data by low/medium/high
  function getGroup(totalRiskScore) {
    if (totalRiskScore < THRESOLD_LOW) return 0 // Low
    if (totalRiskScore < THRESOLD_MEDIUM) return 1 // Medium
    return 2 // High
  }

  const groupedData = useMemo(() => {
    const mergedData: any = [[], [], []]

    dataSourcesRiskScore?.forEach((item) => {
      if (!item) return
      const group = getGroup(item.totalRiskScore)
      mergedData[group].push(item)
    }, {})

    return mergedData
  }, [dataSourcesRiskScore])

  // Define the data ranges and corresponding chart ranges for each bucket:
  const bucketRanges = {
    0: {
      data: { min: 0, max: THRESOLD_LOW },
      chart: { min: 0.25, max: 1.75 }
    },
    1: {
      data: { min: THRESOLD_LOW, max: THRESOLD_MEDIUM },
      chart: { min: 1.75, max: 3.25 }
    },
    2: {
      data: { min: THRESOLD_MEDIUM, max: 100000000 },
      chart: { min: 3.25, max: 4.75 }
    }
  }
  function scaleValue(value, srcMin, srcMax, targetMin, targetMax) {
    return targetMin + ((value - srcMin) / (srcMax - srcMin)) * (targetMax - targetMin)
  }

  const transformedData: any = []
  Object.keys(groupedData).forEach((_, idx) => {
    const groupItems = groupedData[idx]

    groupItems.forEach((item) => {
      let fraction = idx + 1 // default for single-item group
      if (groupItems.length > 1) {
        fraction = scaleValue(
          item.totalRiskScore,
          bucketRanges[idx].data.min,
          bucketRanges[idx].data.max,
          bucketRanges[idx].chart.min,
          bucketRanges[idx].chart.max
        )
      }

      transformedData.push({ ...item, group: fraction })
    })
  })

  if (!isRiskScoreEnabled) return <></>

  return (
    <div className="lb-widget-card ds-risk-score">
      <div className={`risk-score-ds-widget ${className}`} data-test-id="risk-score-ds-widget">
        <Typography type={'strong'} as="div" className="xs-pl-16">
          {intl.formatMessage({ id: 'widget.maindashboard.risk.score.title' })}
        </Typography>
        {dataSourcesRiskScore && (
          <div className="y-axis-tooltips">
            <Typography as={'div'} variant="baseMinus2" className="x-axis-tooltip-word">
              {intl.formatMessage({ id: 'riskScore.mainWidget.highDensity' })}
            </Typography>
            <div className="arrow-vertical">
              <div className="point" />
              <div className="line" />
            </div>
            <Typography as={'div'} variant="baseMinus2" className="x-axis-tooltip-word">
              {intl.formatMessage({ id: 'riskScore.mainWidget.lowDensity' })}
            </Typography>
          </div>
        )}
        {dataSourcesRiskScore ? (
          <div className="risk-score-ds-widget-container">
            <ResponsiveContainer width="100%" height={350} key={resizeKey}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <XAxis
                  type="number"
                  dataKey="group"
                  domain={[0, 5]}
                  padding={{ right: 10, left: 0 }}
                  tick={false}
                  axisLine={false}
                />
                <YAxis
                  dataKey="riskDensity"
                  type="number"
                  tick={false}
                  axisLine={false}
                  padding={{ top: 20, bottom: 0 }}
                  domain={[0, 100]} // Specifies range for risk density
                />
                <Tooltip content={<CustomScatterTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                <Scatter name="low" data={transformedData} shape={<CustomScatterDsElement />} />
              </ScatterChart>
            </ResponsiveContainer>
            <div className="bg-image" />
          </div>
        ) : (
          <NoDataFound />
        )}
        {dataSourcesRiskScore && (
          <div className="x-axis-tooltips">
            <Typography as={'div'} variant="baseMinus2" className="x-axis-tooltip-word">
              {intl.formatMessage({ id: 'riskScore.mainWidget.lowRisk' })}
            </Typography>
            <div className="arrow-horizontal">
              <div className="line" />
              <div className="point" />
            </div>
            <Typography as={'div'} variant="baseMinus2" className="x-axis-tooltip-word">
              {intl.formatMessage({ id: 'riskScore.mainWidget.highRisk' })}
            </Typography>
          </div>
        )}
        <LoaderView showLoader={showLoader} size="small" />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  dataSourcesRiskScore: ownProps?.useMockData
    ? mockMainDashboardRiskScoreWidgetData
    : state.riskScores.dataSourcesRiskScore,
  isRiskScoreEnabled: isRiskScoreEnabled(state),
  showLoader: getShowLoader(ACTION_DATA_SOURCES_RISK_SCORE)
})

const mapDispatchToProps: DispatchProps = { fetchDataSourcesRiskScore }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(RiskScoreDsWidget)
